/**
 * Pet Photo Upload Component
 * Petailor - 宠物照片上传组件
 */

class PetPhotoUpload extends HTMLElement {
  constructor() {
    super();
    this.uploadedUrls = [];
    this.maxFiles = 3;
    this.minFiles = 1;
    this.maxSize = 20 * 1024 * 1024; // 20MB
    this.allowedTypes = ['image/jpeg', 'image/png'];
    this.apiEndpoint = 'https://petailor.lonelyhub.cn/api/upload';
  }

  connectedCallback() {
    this.isSpanish = (this.dataset.locale || document.documentElement.lang || '').toLowerCase().startsWith('es');
    this.messages = this.isSpanish ? {
      maxPhotos: (max) => `Se permite un máximo de ${max} fotos`,
      invalidFormat: 'Sube imágenes en formato JPG o PNG',
      tooLarge: 'Cada imagen debe pesar menos de 20 MB',
      uploading: 'Subiendo fotos...',
      uploadFailed: 'No se pudieron subir las fotos. Inténtalo de nuevo',
      uploaded: (count) => `${count} foto(s) subida(s) correctamente`,
      photoAlt: (index) => `Foto de mascota ${index}`,
      addToCart: 'Añadir al carrito',
      uploadFirst: 'Sube primero una foto de tu mascota',
      count: (current, max) => `${current}/${max} fotos subidas`
    } : {
      maxPhotos: (max) => `Maximum ${max} photos allowed`,
      invalidFormat: 'Please upload JPG/PNG format images',
      tooLarge: 'Image size cannot exceed 20MB',
      uploading: 'Uploading photos...',
      uploadFailed: 'Upload failed, please try again',
      uploaded: (count) => `${count} photo(s) uploaded successfully`,
      photoAlt: (index) => `Pet photo ${index}`,
      addToCart: 'Add to cart',
      uploadFirst: 'Please upload pet photo first',
      count: (current, max) => `${current}/${max} photos uploaded`
    };
    this.dropzone = this.querySelector('.petailor-upload__dropzone');
    this.input = this.querySelector('.petailor-upload__input');
    this.previews = this.querySelector('.petailor-upload__previews');
    this.status = this.querySelector('.petailor-upload__status');
    this.count = this.querySelector('.petailor-upload__count');
    this.form = this.closest('form');
    this.submitBtn = this.form?.querySelector('[type="submit"]');
    this.urlInputs = this.querySelector('.petailor-upload__url-inputs');
    this.variantInput = this.form?.querySelector('.product-variant-id');
    this.petNameInput = this.querySelector('.petailor-upload__pet-name-input');

    this.bindEvents();
    this.bindVariantChangeEvents();
    this.updateSubmitButton();
  }

  bindEvents() {
    // File input change
    this.input?.addEventListener('change', (e) => this.handleFiles(e.target.files));

    // Drag and drop
    this.dropzone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzone.classList.add('dragover');
    });

    this.dropzone?.addEventListener('dragleave', () => {
      this.dropzone.classList.remove('dragover');
    });

    this.dropzone?.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzone.classList.remove('dragover');
      this.handleFiles(e.dataTransfer.files);
    });

    // Pet name input change
    this.petNameInput?.addEventListener('input', () => this.updateUrlInputs());
  }

  bindVariantChangeEvents() {
    // Listen for Shopify variant change events
    document.addEventListener('variant:change', () => this.onVariantChange());

    // Also observe the variant input for changes (fallback)
    if (this.variantInput) {
      const observer = new MutationObserver(() => this.onVariantChange());
      observer.observe(this.variantInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled']
      });
    }

    // Listen for Shopify's product form events
    const productForm = this.closest('product-form');
    if (productForm) {
      productForm.addEventListener('change', () => {
        // Delay to let Shopify update the button first
        setTimeout(() => this.onVariantChange(), 100);
      });
    }
  }

  onVariantChange() {
    // Re-fetch the submit button in case it was replaced
    this.submitBtn = this.form?.querySelector('[type="submit"]');
    this.variantInput = this.form?.querySelector('.product-variant-id');
    this.updateSubmitButton();
  }

  async handleFiles(fileList) {
    const files = Array.from(fileList);

    // Validate file count
    if (this.uploadedUrls.length + files.length > this.maxFiles) {
      this.showStatus('error', this.messages.maxPhotos(this.maxFiles));
      return;
    }

    // Validate each file
    for (const file of files) {
      if (!this.allowedTypes.includes(file.type)) {
        this.showStatus('error', 'Please upload JPG/PNG format images');
        return;
      }
      if (file.size > this.maxSize) {
        this.showStatus('error', 'Image size cannot exceed 20MB');
        return;
      }
    }

    // Upload files
    await this.uploadFiles(files);
  }

  async uploadFiles(files) {
    this.showStatus('uploading', 'Uploading photos...');

    // Show loading placeholders
    this.showLoadingPreviews(files.length);
    this.updateDropzoneState();

    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || this.messages.uploadFailed);
      }

      // Remove loading placeholders and add uploaded URLs
      this.removeLoadingPreviews();
      this.uploadedUrls.push(...result.urls);
      this.renderPreviews();
      this.updateUrlInputs();
      this.updateSubmitButton();
      this.updateDropzoneState();
      this.showStatus('success', this.messages.uploaded(result.count));

      // Clear input
      this.input.value = '';

    } catch (error) {
      console.error('Upload error:', error);
      this.removeLoadingPreviews();
      this.updateDropzoneState();
      this.showStatus('error', (this.isSpanish ? this.messages.uploadFailed : error.message) || this.messages.uploadFailed);
    }
  }

  showLoadingPreviews(count) {
    for (let i = 0; i < count; i++) {
      const placeholder = document.createElement('div');
      placeholder.className = 'petailor-upload__preview petailor-upload__preview--loading';
      placeholder.innerHTML = '<div class="petailor-upload__spinner"></div>';
      this.previews.appendChild(placeholder);
    }
  }

  removeLoadingPreviews() {
    this.previews.querySelectorAll('.petailor-upload__preview--loading').forEach(el => el.remove());
  }

  updateDropzoneState() {
    const isFull = this.uploadedUrls.length >= this.maxFiles;
    if (this.dropzone) {
      this.dropzone.classList.toggle('petailor-upload__dropzone--disabled', isFull);
      this.input.disabled = isFull;
    }
  }

  renderPreviews() {
    this.previews.innerHTML = this.uploadedUrls.map((url, index) => `
      <div class="petailor-upload__preview petailor-upload__preview--image-loading" data-index="${index}">
        <div class="petailor-upload__spinner"></div>
        <img src="${url}" alt="${this.messages.photoAlt(index + 1)}">
        <button type="button" class="petailor-upload__preview-remove" data-index="${index}">&times;</button>
      </div>
    `).join('');

    // Bind remove buttons
    this.previews.querySelectorAll('.petailor-upload__preview-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(btn.dataset.index);
        this.removePhoto(index);
      });
    });

    // Handle image load events
    this.previews.querySelectorAll('img').forEach(img => {
      img.addEventListener('load', () => {
        img.closest('.petailor-upload__preview').classList.remove('petailor-upload__preview--image-loading');
      });
      // If image already cached, remove loading state
      if (img.complete) {
        img.closest('.petailor-upload__preview').classList.remove('petailor-upload__preview--image-loading');
      }
    });

    this.updateCount();
  }

  removePhoto(index) {
    this.uploadedUrls.splice(index, 1);
    this.renderPreviews();
    this.updateUrlInputs();
    this.updateSubmitButton();
    this.updateDropzoneState();
    this.hideStatus();
  }

  updateUrlInputs() {
    // Create hidden inputs for line item properties
    const photoInputs = this.uploadedUrls.map((url, index) => `
      <input type="hidden" name="properties[Pet Photo ${index + 1}]" value="${url}">
    `).join('');

    // Add pet name if provided
    const petName = this.petNameInput?.value?.trim() || '';
    const petNameInput = petName ? `<input type="hidden" name="properties[Pet Name]" value="${petName}">` : '';

    this.urlInputs.innerHTML = photoInputs + petNameInput;
  }

  updateSubmitButton() {
    if (this.submitBtn) {
      const hasEnoughPhotos = this.uploadedUrls.length >= this.minFiles;
      const spanEl = this.submitBtn.querySelector('span');
      if (hasEnoughPhotos) {
        this.submitBtn.disabled = false;
        if (spanEl) spanEl.textContent = this.messages.addToCart;
      } else {
        this.submitBtn.disabled = true;
        if (spanEl) spanEl.textContent = this.messages.uploadFirst;
      }
    }
  }

  updateCount() {
    if (this.count) {
      this.count.textContent = this.messages.count(this.uploadedUrls.length, this.maxFiles);
    }
  }

  showStatus(type, message) {
    if (this.status) {
      this.status.className = `petailor-upload__status petailor-upload__status--${type}`;
      this.status.textContent = message;
      this.status.hidden = false;
    }
  }

  hideStatus() {
    if (this.status) {
      this.status.hidden = true;
    }
  }
}

customElements.define('pet-photo-upload', PetPhotoUpload);
